import React from "react";

import { Box, useTheme } from "@mui/material";

// ── Types ────────────────────────────────────────────────────────────────────

export type RushaChipVariant =
  "success" | "error" | "warning" | "info" | "default";

export type RushaChipSize = "sm" | "md";

interface RushaChipProps {
  label: string;
  variant?: RushaChipVariant;
  /** "sm" = pipeline run header chips (mono, no border), "md" = status chips */
  size?: RushaChipSize;
  /** Monospace font (for branch/SHA chips in run header) */
  mono?: boolean;
  /** Link href (renders as <a>) */
  href?: string;
  /** Open link in new tab */
  target?: string;
}

// ── Color tokens matching mockup .rusha-chip-* ───────────────────────────────

const variantColors: Record<
  RushaChipVariant,
  { bg: string; color: string; border: string }
> = {
  success: {
    bg: "rgba(16, 185, 129, 0.12)",
    color: "#10b981",
    border: "rgba(16, 185, 129, 0.3)",
  },
  error: {
    bg: "rgba(244, 63, 94, 0.12)",
    color: "#f43f5e",
    border: "rgba(244, 63, 94, 0.3)",
  },
  warning: {
    bg: "rgba(245, 158, 11, 0.12)",
    color: "#f59e0b",
    border: "rgba(245, 158, 11, 0.3)",
  },
  info: {
    bg: "rgba(59, 130, 246, 0.12)",
    color: "#3b82f6",
    border: "rgba(59, 130, 246, 0.3)",
  },
  default: {
    bg: "rgba(148, 163, 184, 0.15)",
    color: "#64748b",
    border: "rgba(148, 163, 184, 0.2)",
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * RushaChip — shared chip component matching the mockup's chip system.
 *
 * Two sizes:
 *   - "md" (default): matches `.rusha-chip` — 12px border-radius, 3px 10px padding,
 *     12px font, 600 weight, 1px border. Used for status badges (Running, Failed).
 *   - "sm": matches `.rusha-pl-chip` — 6px border-radius, 2px 8px padding, 11px mono
 *     font, no border. Used for branch/SHA/mode chips in the pipeline run header.
 */
export function RushaChip({
  label,
  variant = "default",
  size = "md",
  mono = false,
  href,
  target,
}: RushaChipProps): React.ReactElement {
  const theme = useTheme();
  const colors = variantColors[variant];

  const isSm = size === "sm";

  const sx = {
    display: "inline-flex",
    alignItems: "center",
    padding: isSm ? "2px 8px" : "3px 10px",
    borderRadius: isSm ? "6px" : "12px",
    fontSize: isSm ? "11px" : "12px",
    fontWeight: isSm ? 400 : 600,
    fontFamily: mono ? "monospace" : "inherit",
    bgcolor: colors.bg,
    color: isSm ? theme.palette.text.secondary : colors.color,
    border: isSm ? "none" : `1px solid ${colors.border}`,
    textDecoration: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };

  if (href) {
    return (
      <Box component="a" href={href} target={target} sx={sx}>
        {label}
      </Box>
    );
  }

  return <Box sx={sx}>{label}</Box>;
}

export default RushaChip;
